import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithGoogle, auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { Box, TextField, Button, Typography, Alert, Paper, Divider, Link } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signInWithPopup, OAuthProvider } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

import MFAVerificationDialog from './MFAVerificationDialog';
import { twoFactorService } from '../services/twoFactorService';
import logger from '../utils/logger';
import { isInviteOnlyMode } from '../utils/inviteOnlyMode';
import { userDocHasFamilyContext } from '../utils/familyScope';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();
  const functions = getFunctions();
  const sendPasswordResetEmail = httpsCallable(functions, 'sendPasswordResetEmail');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      // AuthContext's onAuthStateChanged will gate the app with loading,
      // fetch Firestore data, then route guards redirect automatically.
    } catch (error) {
      // Check if MFA verification is required
      if (error.code === 'auth/multi-factor-auth-required') {
        try {
          const resolverData = twoFactorService.getMFAResolver(error);
          setMfaResolver(resolverData);
          setMfaDialogOpen(true);
        } catch (mfaError) {
          setError('Failed to initialize two-factor authentication');
        }
      } else {
        setError(error.message);
      }
      setLoading(false);
    }
  };

  const handleMFASuccess = () => {
    setMfaDialogOpen(false);
    setMfaResolver(null);
    // AuthContext will pick up the auth state change and route guards redirect.
  };

  const handleMFAClose = () => {
    setMfaDialogOpen(false);
    setMfaResolver(null);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const user = await signInWithGoogle();

      // Immediately check Firestore to distinguish existing vs new user.
      // This prevents the route guard from bouncing the user through the
      // signup wizard while AuthContext is still loading Firestore data.
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.accountType === 'system_admin') {
          navigate('/system-admin/dashboard');
          return;
        }
        if (userDocHasFamilyContext(data)) {
          navigate('/');
          return;
        }
      }

      // New user — no Firestore doc or incomplete profile.
      if (isInviteOnlyMode()) {
        await signOut(auth);
        setError('Registration is by invitation only. Please ask your family administrator for an invitation link.');
        setLoading(false);
        return;
      }
      navigate('/signup');
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        try {
          const resolverData = twoFactorService.getMFAResolver(error);
          setMfaResolver(resolverData);
          setMfaDialogOpen(true);
        } catch (mfaError) {
          setError('Failed to initialize two-factor authentication');
        }
      } else {
        setError('Failed to sign in with Google. Please try again.');
        logger.error('Google sign-in error:', error);
      }
      setLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        prompt: 'select_account',
        tenant: 'common'
      });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Immediately check Firestore to distinguish existing vs new user.
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.accountType === 'system_admin') {
          navigate('/system-admin/dashboard');
          return;
        }
        if (userDocHasFamilyContext(data)) {
          navigate('/');
          return;
        }
      }

      // New user — no Firestore doc or incomplete profile.
      if (isInviteOnlyMode()) {
        await signOut(auth);
        setError('Registration is by invitation only. Please ask your family administrator for an invitation link.');
        setLoading(false);
        return;
      }
      navigate('/signup');
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        try {
          const resolverData = twoFactorService.getMFAResolver(error);
          setMfaResolver(resolverData);
          setMfaDialogOpen(true);
        } catch (mfaError) {
          setError('Failed to initialize two-factor authentication');
        }
      } else {
        setError('Failed to sign in with Microsoft. Please try again.');
        logger.error('Microsoft sign-in error:', error);
      }
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail({ email });
      setResetSent(true);
      setShowResetForm(false);
    } catch (error) {
      setError('Failed to send password reset email. Please try again.');
      logger.error('Password reset error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Login
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {resetSent && <Alert severity="success" sx={{ mb: 2 }}>Password reset email sent! Please check your inbox.</Alert>}
        
        {!showResetForm ? (
          <>
            <form onSubmit={handleSubmit}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login with Email'}
              </Button>
            </form>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowResetForm(true)}
                sx={{ textDecoration: 'none' }}
              >
                Forgot Password?
              </Link>
            </Box>
            
            <Divider sx={{ my: 3 }}>or</Divider>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                {loading ? 'Signing in...' : 'Sign in with Google'}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleMicrosoftSignIn}
                disabled={loading}
                sx={{
                  borderColor: '#00a4ef',
                  color: '#00a4ef',
                  '&:hover': {
                    borderColor: '#0078d4',
                    backgroundColor: 'rgba(0, 164, 239, 0.04)',
                  },
                }}
              >
                {loading ? 'Signing in...' : 'Sign in with Microsoft'}
              </Button>
            </Box>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {isInviteOnlyMode()
                  ? 'Registration is by invitation only. Contact your family administrator for an invite link.'
                  : "Don't have an account? Sign up or ask your family admin for an invite link."}
              </Typography>
            </Box>
          </>
        ) : (
          <form onSubmit={handlePasswordReset}>
            <Typography variant="h5" component="h2" gutterBottom align="center">
              Reset Password
            </Typography>
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => setShowResetForm(false)}
              sx={{ mt: 2 }}
            >
              Back to Login
            </Button>
          </form>
        )}
      </Paper>

      {/* MFA Verification Dialog */}
      <MFAVerificationDialog
        open={mfaDialogOpen}
        onClose={handleMFAClose}
        mfaResolver={mfaResolver}
        onSuccess={handleMFASuccess}
      />
    </Box>
  );
}

export default Login; 